import { useEffect, useState } from 'react';
import type React from 'react';
import {
  listAdminFamilies,
  createFamily,
  updateFamily,
  deleteFamily,
  type AdminFamily,
} from '../../lib/adminApi';
import { AdminPageShell, cls } from './shared';

export function FamiliesPage() {
  const [families, setFamilies] = useState<AdminFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newName, setNewName] = useState('');
  const [newSortOrder, setNewSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  function load() {
    setLoading(true);
    listAdminFamilies()
      .then((r) => {
        setFamilies(r.families);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleCreate() {
    if (!newSlug.trim() || !newName.trim()) return;
    setSaving(true);
    try {
      await createFamily({ slug: newSlug.trim(), name: newName.trim(), sort_order: newSortOrder });
      setShowCreate(false);
      setNewSlug('');
      setNewName('');
      setNewSortOrder(0);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error creating family');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(f: AdminFamily) {
    if (!confirm(`Delete family "${f.name}"? This cannot be undone.`)) return;
    try {
      await deleteFamily(f.id);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error deleting family');
    }
  }

  if (loading) return <AdminPageShell title="Families"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="Families"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  return (
    <AdminPageShell
      title="Families"
      actions={
        <button
          onClick={() => setShowCreate(true)}
          className={cls.btnPrimary}
        >
          + New Family
        </button>
      }
    >
      {showCreate && (
        <div className={cls.formPanel}>
          <p className="text-sm font-medium text-vari-navy mb-3">New Family</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs text-vari-gray block mb-1">Slug</label>
              <input
                className={cls.input}
                placeholder="e.g. open-office"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Name</label>
              <input
                className={cls.input}
                placeholder="e.g. Open Office"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Sort Order</label>
              <input
                type="number"
                className={cls.input}
                value={newSortOrder}
                onChange={(e) => setNewSortOrder(parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void handleCreate()}
              disabled={saving}
              className={cls.btnSmPrimary}
            >
              {saving ? 'Saving…' : 'Create'}
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className={cls.btnGhost}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>Slug</th>
              <th className={cls.th}>Name</th>
              <th className={cls.th}>Sort</th>
              <th className={cls.th}>Layouts</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {families.map((f, i) => (
              <FamilyRow
                key={f.id}
                family={f}
                striped={i % 2 === 1}
                onDelete={() => void handleDelete(f)}
                onSaved={load}
              />
            ))}
            {families.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No families yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminPageShell>
  );
}

function FamilyRow({
  family,
  striped,
  onDelete,
  onSaved,
}: {
  family: AdminFamily;
  striped: boolean;
  onDelete: () => void;
  onSaved: () => void;
}) {
  const [editName, setEditName] = useState(family.name);
  const [editSort, setEditSort] = useState(family.sortOrder);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateFamily(family.id, { name: editName, sort_order: editSort });
      setEditing(false);
      onSaved();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr className={cls.tr(striped)}>
      <td className={cls.tdMono}>{family.slug}</td>
      <td className={cls.td}>
        {editing ? (
          <input
            className="border border-vari-soft-gray rounded px-2 py-0.5 text-sm w-48"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') setEditing(false); }}
            autoFocus
          />
        ) : (
          <span
            className="cursor-pointer hover:text-vari-navy hover:underline"
            onClick={() => setEditing(true)}
          >
            {family.name}
          </span>
        )}
      </td>
      <td className={cls.td}>
        {editing ? (
          <input
            type="number"
            className="border border-vari-soft-gray rounded px-2 py-0.5 text-sm w-20"
            value={editSort}
            onChange={(e) => setEditSort(parseInt(e.target.value, 10) || 0)}
          />
        ) : (
          <span className="text-vari-gray">{family.sortOrder}</span>
        )}
      </td>
      <td className={`${cls.td} text-vari-gray`}>{family.layoutCount}</td>
      <td className={`${cls.tdRight} space-x-2`}>
        {editing ? (
          <>
            <button
              onClick={() => void save()}
              disabled={saving}
              className={cls.btnSmPrimary}
            >
              {saving ? '…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className={cls.btnGhost}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} className={cls.btnGhost}>
              Edit
            </button>
            {confirmDelete ? (
              <>
                <button
                  onClick={onDelete}
                  className={cls.btnConfirm}
                >
                  Confirm?
                </button>
                <button onClick={() => setConfirmDelete(false)} className={cls.btnGhost}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={family.layoutCount > 0}
                className={`${cls.btnDanger} disabled:opacity-30 disabled:cursor-not-allowed`}
                title={family.layoutCount > 0 ? `${family.layoutCount} layouts use this family` : undefined}
              >
                Delete
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}
